import dayjs from 'dayjs';

/** Quantidade mínima que produz uma projeção legível. */
const MIN_AMOUNT = 2;

/** Projeção das parcelas que a recorrência vai criar. */
export interface RecurrencePreview {
  /** Número da primeira parcela. */
  readonly from: number;
  /** Número da última parcela. */
  readonly to: number;
  /** Vencimento da primeira, em `DD/MM/YYYY`. */
  readonly firstDate: string;
  /** Vencimento da última, em `DD/MM/YYYY`. */
  readonly lastDate: string;
}

/**
 * Projeta a faixa de parcelas criada pela recorrência.
 *
 * O endpoint gera cópias com **+1 mês no vencimento e +1 no número** cada, então a
 * projeção é aritmética. `amount` é tratado como o **total** de parcelas criadas,
 * contando a original — a leitura mais direta de `amountOfParcel`.
 *
 * É explicitamente uma projeção da tela: não existe endpoint de pré-checagem de
 * número de parcela, então o backend continua podendo recusar a criação (número já
 * usado no mesmo tipo). Quem consome deve rotular o resultado como estimativa.
 *
 * `dayjs().add(n, 'month')` já resolve mês curto: 31/01 + 1 mês vira 28/02.
 *
 * @param numberParcel - Número da primeira parcela.
 * @param expirationDate - Vencimento da primeira, em ISO (`YYYY-MM-DD`).
 * @param amount - Quantidade total de parcelas a criar.
 * @returns A projeção, ou `null` quando falta dado para projetar.
 *
 * @example
 * ```ts
 * deriveRecurrencePreview(13, '2026-09-10', 6);
 * // { from: 13, to: 18, firstDate: '10/09/2026', lastDate: '10/02/2027' }
 * ```
 */
export function deriveRecurrencePreview(
  numberParcel: number | null | undefined,
  expirationDate: string | null | undefined,
  amount: number | null | undefined,
): RecurrencePreview | null {
  if (numberParcel === null || numberParcel === undefined || numberParcel < 1) return null;
  if (amount === null || amount === undefined || amount < MIN_AMOUNT) return null;

  const first = dayjs(expirationDate ?? '');
  if (!first.isValid()) return null;

  const copies = Math.floor(amount) - 1;

  return {
    from: numberParcel,
    to: numberParcel + copies,
    firstDate: first.format('DD/MM/YYYY'),
    lastDate: first.add(copies, 'month').format('DD/MM/YYYY'),
  };
}
